'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { ACTIVATION_ORDER_TOAST_Z_INDEX, getToastZIndex, } from '#pplx/frontend/aether/components/elevation/index.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Motion } from '#pplx/frontend/aether/components/Motion/index.js';
import { Surface } from '#pplx/frontend/aether/components/Surface/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { PortalPrimitive } from '#pplx/frontend/aether/primitives/PortalPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
// eslint-disable-next-line no-restricted-imports -- Branches the toast viewport out of Radix modal dismissal (no aether equivalent); this legacy renderer is retired by the aether/toasts migration, whose renderer lives inside aether.
import { DismissableLayerBranch } from '@radix-ui/react-dismissable-layer';
import classNames from 'classnames';
import { noop } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
// pointerEvents is inline (not a class) so it wins under a Radix modal's inline
// body-level pointer-events lock and holds in stylesheet-less environments.
const TOAST_CONTAINER_STYLE = {
    zIndex: getToastZIndex(),
    pointerEvents: 'none',
};
const TOAST_CARD_STYLE = { pointerEvents: 'auto' };
/**
 * @deprecated Prefer Aether's `useToastContext().openToast()`. Direct rendering remains supported.
 */
const Toast = React.memo(function Toast({ keyProp = 0, message, variant, description, ctaText, ctaOnClick, ctaVariant = 'text', secondaryCtaText, secondaryCtaOnClick, closeOnCtaClick = true, closeOnSecondaryCtaClick = true, isVisible = false, timeout, handleClick, handleClose, onTimeout = noop, iconOverride, messageLineClamp = 2, descriptionLineClamp = 2, embedded = false, animateIcon = false, ...divProps }) {
    const { $t } = useIntl();
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const [isVisibleInternal, setIsVisibleInternal] = useState(isVisible);
    const toastContainerStyle = shouldUseActivationOrder
        ? { ...TOAST_CONTAINER_STYLE, zIndex: ACTIVATION_ORDER_TOAST_Z_INDEX }
        : TOAST_CONTAINER_STYLE;
    // Set internal state once the component has mounted
    useEffect(() => {
        setIsVisibleInternal(isVisible);
    }, [isVisible]);
    // Determine if toast should auto-dismiss: timeout must be a positive number
    const hasAutoTimeout = timeout !== null && timeout !== undefined && timeout > 0;
    // Optional timer to hide the toast after a certain amount of time
    useEffect(() => {
        let timer;
        if (isVisible && hasAutoTimeout) {
            timer = setTimeout(() => {
                setIsVisibleInternal(false);
                onTimeout();
            }, timeout * 1000);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [isVisibleInternal, timeout, isVisible, onTimeout, hasAutoTimeout]);
    const onClose = useCallback((e, reason = 'close-button') => {
        // Stop propagation so the action button click doesn't trigger the parent handler
        e?.stopPropagation();
        setIsVisibleInternal(false);
        handleClose?.({ reason });
    }, [handleClose]);
    const onCtaClick = useCallback((e) => {
        // Stop propagation so the action button click doesn't trigger the parent handler
        e?.stopPropagation();
        ctaOnClick?.();
        if (closeOnCtaClick) {
            onClose(undefined, 'cta');
        }
    }, [ctaOnClick, onClose, closeOnCtaClick]);
    const onSecondaryCtaClick = useCallback((e) => {
        // Stop propagation so the action button click doesn't trigger the parent handler
        e?.stopPropagation();
        secondaryCtaOnClick?.();
        if (closeOnSecondaryCtaClick) {
            onClose(undefined, 'cta');
        }
    }, [closeOnSecondaryCtaClick, onClose, secondaryCtaOnClick]);
    const hasSecondaryCta = Boolean(secondaryCtaText && secondaryCtaOnClick);
    const icon = useMemo(() => {
        if (iconOverride) {
            return iconOverride;
        }
        switch (variant) {
            case 'success':
                return $icon('check');
            case 'error':
                return $icon('alert-circle');
            case 'refresh':
                return $icon('refresh');
            case 'warning':
                return $icon('alert-circle');
            case 'neutral':
                return null;
        }
    }, [iconOverride, variant]);
    const innerContent = (_jsx(Surface, { elevation: "toast", className: "relative", style: TOAST_CARD_STYLE, children: _jsx("div", { ...divProps, children: _jsxs(BoxPrimitive, { display: "flex", align: "center", bg: "raised", padding: "md", rounded: "lg", className: classNames('shadow-md w-[340px] gap-3', {
                    'cursor-pointer': variant === 'refresh',
                }), onClick: handleClick ?? noop, children: [icon && (_jsx(BoxPrimitive, { rounded: "md", display: "inline-flex", align: "center", justify: "center", bg: "subtle", className: "dark:bg-soft size-8 shrink-0", children: _jsx(TextPrimitive, { variant: "smallBold", className: classNames('inline-flex', { 'animate-spin': animateIcon }), color: variant === 'error' ? 'caution' : variant === 'warning' ? 'warning' : 'super', children: _jsx(Icon, { icon: icon, size: "small" }) }) })), _jsxs("div", { className: "flex min-w-0 flex-1 flex-col gap-1", children: [_jsxs("div", { className: "flex min-w-0 flex-1 items-center gap-1", children: [_jsxs(BoxPrimitive, { display: "flex", direction: "col", className: "min-w-0 flex-1 gap-two", children: [_jsx(TextPrimitive, { leading: "tight", variant: "small", color: "default", lineClamp: messageLineClamp, children: message }), description && (_jsx(TextPrimitive, { variant: "tinyRegular", color: "light", whitespace: "pre-line", lineClamp: descriptionLineClamp, children: description }))] }), !hasSecondaryCta && ctaText && ctaOnClick && (_jsx(Button, { variant: ctaVariant, size: "small", onClick: onCtaClick, children: ctaText })), _jsx("div", { className: "-mr-2", children: _jsx(Button, { variant: "text", size: "small", icon: $icon('x'), onClick: onClose, "aria-label": $t({
                                                defaultMessage: 'Close',
                                                id: '7e14727493',
                                                description: 'Action button label in the Close Button shared UI section',
                                            }) }) })] }), hasSecondaryCta && secondaryCtaText && (_jsxs("div", { className: "flex justify-end gap-0", children: [_jsx(Button, { variant: "text", size: "small", onClick: onSecondaryCtaClick, children: secondaryCtaText }), ctaText && ctaOnClick && (_jsx(Button, { variant: ctaVariant, size: "small", onClick: onCtaClick, children: ctaText }))] }))] })] }) }) }));
    if (embedded) {
        return innerContent;
    }
    return (_jsx(PortalPrimitive, { children: _jsx(DismissableLayerBranch, { style: toastContainerStyle, className: classNames('right-toastHMargin top-toastVMargin fixed flex items-center justify-center', { 'cursor-pointer': handleClick }), "aria-live": "polite", "aria-atomic": false, children: _jsx(Motion.Slide, { isVisible: isVisibleInternal, side: "right", children: innerContent }, keyProp) }) }));
});
export default Toast;
//# sourceMappingURL=Toast.js.map